import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SubfieldDefinition } from '../../types/domain';
import {
  Copy,
  Check,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  FileCode,
  X,
} from 'lucide-react';
import { PortalPopover } from '../ui/PortalPopover';
import { Button } from '../ui/Button';

export interface JsonCodeEditorProps {
  initialValue: any;
  subfields?: SubfieldDefinition[];
  maxItems?: number;
  disabled?: boolean;
  minHeight?: number;
  maxHeight?: number;
  onChange?: (parsedValue: any, isValid: boolean, errors: string[]) => void;
}

interface JsonToken {
  text: string;
  type: 'key' | 'string' | 'number' | 'boolean' | 'null' | 'punctuation' | 'plain';
}

function tokenizeJsonLine(line: string): JsonToken[] {
  const tokens: JsonToken[] = [];
  const regex =
    /("(?:[^"\\]|\\.)*")(\s*:)|("(?:[^"\\]|\\.)*")|(\b(?:true|false|null)\b)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],:])/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: line.slice(lastIndex, match.index), type: 'plain' });
    }

    if (match[1]) {
      // Key followed by colon
      tokens.push({ text: match[1], type: 'key' });
      if (match[2]) {
        tokens.push({ text: match[2], type: 'punctuation' });
      }
    } else if (match[3]) {
      // String value
      tokens.push({ text: match[3], type: 'string' });
    } else if (match[4]) {
      const val = match[4];
      tokens.push({ text: val, type: val === 'null' ? 'null' : 'boolean' });
    } else if (match[5]) {
      tokens.push({ text: match[5], type: 'number' });
    } else if (match[6]) {
      tokens.push({ text: match[6], type: 'punctuation' });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < line.length) {
    tokens.push({ text: line.slice(lastIndex), type: 'plain' });
  }

  return tokens;
}

function getTokenClass(type: JsonToken['type']): string {
  switch (type) {
    case 'key':
      return 'text-sky-400 dark:text-sky-300 font-semibold';
    case 'string':
      return 'text-emerald-400 dark:text-emerald-300';
    case 'number':
      return 'text-amber-400 dark:text-amber-300';
    case 'boolean':
      return 'text-purple-400 dark:text-purple-300 font-medium';
    case 'null':
      return 'text-zinc-500 italic';
    case 'punctuation':
      return 'text-zinc-400';
    case 'plain':
    default:
      return 'text-zinc-200';
  }
}

/**
 * Validates a parsed object or array against defined subfields and schema constraints.
 */
function validateSchema(
  value: any,
  subfields?: SubfieldDefinition[],
  maxItems?: number
): string[] {
  const errors: string[] = [];
  if (!subfields || subfields.length === 0) {
    if (Array.isArray(value) && maxItems && maxItems > 0 && value.length > maxItems) {
      errors.push(`Array exceeds maximum limit of ${maxItems} items (received ${value.length})`);
    }
    return errors;
  }

  const allowedKeys = subfields.map((s) => s.key.toLowerCase());
  const allowedDisplay = subfields.map((s) => s.name ? `${s.name} (${s.key})` : s.key).join(', ');

  const validateObject = (obj: any, path: string) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      errors.push(`${path || 'Field'} must be an object`);
      return;
    }

    // 1. Check for unmapped fields outside of schema
    for (const key of Object.keys(obj)) {
      if (!allowedKeys.includes(key.toLowerCase())) {
        const fieldName = path ? `${path}.${key}` : key;
        errors.push(
          `Field "${fieldName}" is outside of schema. Allowed properties: ${allowedDisplay}`
        );
      }
    }

    // 2. Check each defined subfield (required check & data type check)
    for (const sub of subfields) {
      // Find matching key case-insensitively
      const actualKey = Object.keys(obj).find((k) => k.toLowerCase() === sub.key.toLowerCase());
      const val = actualKey !== undefined ? obj[actualKey] : undefined;
      const fieldPath = path ? `${path}.${sub.key}` : sub.key;

      if (sub.is_required) {
        if (val === undefined || val === null || val === '') {
          errors.push(`Field "${fieldPath}" (${sub.name || sub.key}) is required`);
          continue;
        }
      }

      if (val === undefined || val === null || val === '') {
        continue;
      }

      // Check types
      switch (sub.data_type?.toLowerCase()) {
        case 'number': {
          const num = Number(val);
          if (typeof val === 'boolean' || isNaN(num)) {
            errors.push(`Field "${fieldPath}" must be a numeric value`);
          }
          break;
        }
        case 'boolean': {
          if (typeof val !== 'boolean' && val !== 'true' && val !== 'false' && val !== 1 && val !== 0) {
            errors.push(`Field "${fieldPath}" must be a boolean (true/false)`);
          }
          break;
        }
        case 'email': {
          if (typeof val !== 'string' || !val.includes('@') || !val.includes('.')) {
            errors.push(`Field "${fieldPath}" must be a valid email address`);
          }
          break;
        }
        case 'uuid': {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (typeof val !== 'string' || !uuidRegex.test(val)) {
            errors.push(`Field "${fieldPath}" must be a valid UUID`);
          }
          break;
        }
        case 'date': {
          if (typeof val !== 'string' || isNaN(Date.parse(val))) {
            errors.push(`Field "${fieldPath}" must be a valid date string`);
          }
          break;
        }
      }
    }
  };

  if (Array.isArray(value)) {
    if (maxItems && maxItems > 0 && value.length > maxItems) {
      errors.push(`Array exceeds maximum limit of ${maxItems} items (received ${value.length})`);
    }
    value.forEach((item, idx) => {
      validateObject(item, `Item #${idx + 1}`);
    });
  } else if (value !== null && typeof value === 'object') {
    validateObject(value, '');
  }

  return errors;
}

export const JsonCodeEditor: React.FC<JsonCodeEditorProps> = ({
  initialValue,
  subfields,
  maxItems,
  disabled = false,
  minHeight = 160,
  maxHeight = 320,
  onChange,
}) => {
  const [jsonText, setJsonText] = useState<string>(() => {
    if (initialValue === undefined || initialValue === null) return '';
    return typeof initialValue === 'object'
      ? JSON.stringify(initialValue, null, 2)
      : String(initialValue);
  });

  const [copied, setCopied] = useState(false);
  const [syntaxError, setSyntaxError] = useState<string | null>(null);
  const [schemaErrors, setSchemaErrors] = useState<string[]>([]);
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const schemaBtnRef = useRef<HTMLButtonElement>(null);

  // Split lines for line numbers and syntax highlighting
  const lines = useMemo(() => jsonText.split('\n'), [jsonText]);

  // Synchronized scrolling
  const handleScroll = () => {
    if (!textareaRef.current) return;
    const { scrollTop, scrollLeft } = textareaRef.current;
    if (preRef.current) {
      preRef.current.scrollTop = scrollTop;
      preRef.current.scrollLeft = scrollLeft;
    }
    if (gutterRef.current) {
      gutterRef.current.scrollTop = scrollTop;
    }
  };

  // Real-time validation
  useEffect(() => {
    if (!jsonText.trim()) {
      setSyntaxError('Payload cannot be empty');
      setSchemaErrors([]);
      onChange?.(null, false, ['Payload cannot be empty']);
      return;
    }

    try {
      const parsed = JSON.parse(jsonText);
      setSyntaxError(null);

      // Validate schema
      const sErrors = validateSchema(parsed, subfields, maxItems);
      setSchemaErrors(sErrors);

      const isValid = sErrors.length === 0;
      onChange?.(parsed, isValid, sErrors);
    } catch (err: any) {
      const msg = err.message || 'Invalid JSON syntax';
      setSyntaxError(msg);
      setSchemaErrors([]);
      onChange?.(null, false, [msg]);
    }
  }, [jsonText, subfields, maxItems]);

  // Tab key indentation support
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      if (e.shiftKey) {
        // Shift+Tab: dedent 2 spaces if preceding text has them
        const lineStart = jsonText.lastIndexOf('\n', start - 1) + 1;
        if (jsonText.slice(lineStart, lineStart + 2) === '  ') {
          const updated = jsonText.slice(0, lineStart) + jsonText.slice(lineStart + 2);
          setJsonText(updated);
          setTimeout(() => {
            textarea.selectionStart = Math.max(start - 2, lineStart);
            textarea.selectionEnd = Math.max(end - 2, lineStart);
          }, 0);
        }
      } else {
        // Tab: insert 2 spaces
        const updated = jsonText.substring(0, start) + '  ' + jsonText.substring(end);
        setJsonText(updated);
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + 2;
        }, 0);
      }
    }
  };

  // Prettify / Format JSON
  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonText(formatted);
    } catch {
      // cannot format invalid JSON
    }
  };

  // Copy code
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  const allErrors = syntaxError ? [syntaxError] : schemaErrors;
  const isValid = allErrors.length === 0;

  return (
    <div className="flex flex-col rounded-md border border-cf-border bg-[#0d0d0d] overflow-hidden text-xs">
      {/* Editor Sub-Header Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#141414] border-b border-cf-border select-none">
        <div className="flex items-center gap-2">
          {isValid ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-mono font-medium">
              <CheckCircle2 size={11} />
              <span>Valid Schema</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded bg-rose-500/15 text-rose-400 border border-rose-500/30 text-[10px] font-mono font-medium">
              <AlertCircle size={11} />
              <span>
                {syntaxError ? 'Syntax Error' : `${schemaErrors.length} Schema Error${schemaErrors.length > 1 ? 's' : ''}`}
              </span>
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {subfields && subfields.length > 0 && (
            <Button
              ref={schemaBtnRef}
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => setIsSchemaOpen((prev) => !prev)}
              className={`text-[10px] font-mono ${
                isSchemaOpen
                  ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40'
                  : 'bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 hover:text-white'
              }`}
              icon={<FileCode size={10} className="text-sky-400" />}
              title="View allowed schema attributes and data types"
            >
              <span>Schema</span>
              <span className="px-1 py-0.2 rounded bg-zinc-900 text-zinc-400 text-[9px]">
                {subfields.length}
              </span>
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={handleFormat}
            disabled={disabled || !!syntaxError}
            className="bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 hover:text-white text-[10px] font-mono"
            icon={<Sparkles size={10} className="text-amber-400" />}
            title="Prettify and format JSON indentation"
          >
            <span>Format</span>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={handleCopy}
            className="bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 hover:text-white text-[10px] font-mono"
            icon={copied ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
            title="Copy JSON to clipboard"
          >
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </Button>
        </div>
      </div>

      {/* Schema Popover via Portal */}
      {subfields && subfields.length > 0 && isSchemaOpen && (
        <PortalPopover
          isOpen={isSchemaOpen}
          onClose={() => setIsSchemaOpen(false)}
          triggerRef={schemaBtnRef}
          align="right"
          className="w-[340px] max-w-[92vw] shadow-2xl border border-cf-border bg-[#141414] rounded-lg overflow-hidden z-50 text-xs"
        >
          {/* Popover Header */}
          <div className="flex items-center justify-between px-3 py-2 bg-[#1a1a1a] border-b border-cf-border select-none">
            <div className="flex items-center gap-2">
              <FileCode size={13} className="text-sky-400 shrink-0" />
              <span className="font-semibold text-xs text-cf-textPrimary">Expected Schema</span>
              <span className="px-1.5 py-0.2 rounded bg-zinc-800 text-zinc-300 font-mono text-[10px] border border-zinc-700">
                {subfields.length} {subfields.length === 1 ? 'field' : 'fields'}
              </span>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setIsSchemaOpen(false)}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
              title="Close schema popover"
            >
              <X size={13} />
            </Button>
          </div>

          {/* Max items banner if array constraint */}
          {maxItems && maxItems > 0 && (
            <div className="px-3 py-1.5 bg-blue-500/10 border-b border-blue-500/20 text-[11px] font-mono text-blue-300 flex items-center justify-between">
              <span>Array Limit:</span>
              <span className="font-semibold">Max {maxItems} items</span>
            </div>
          )}

          {/* Popover Content Body */}
          <div className="max-h-[260px] overflow-y-auto divide-y divide-zinc-800/60">
            {subfields.map((sub) => (
              <div key={sub.id} className="p-2.5 hover:bg-zinc-900/50 transition-colors space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-mono text-sky-400 font-semibold text-[11px] truncate">
                      {sub.key}
                    </span>
                    {sub.name && sub.name !== sub.key && (
                      <span className="text-zinc-400 text-[11px] truncate">
                        · {sub.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono text-[9px] border border-zinc-700/60">
                      {sub.data_type}
                    </span>
                    <span
                      className={`px-1.5 py-0.5 rounded font-mono text-[9px] ${
                        sub.is_required
                          ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40'
                          : 'text-zinc-500'
                      }`}
                    >
                      {sub.is_required ? 'Required' : 'Optional'}
                    </span>
                  </div>
                </div>
                {sub.sample_value && (
                  <div className="text-[10px] text-zinc-500 font-mono truncate pl-1">
                    Sample: <span className="text-zinc-400">"{sub.sample_value}"</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Popover Footer */}
          <div className="px-3 py-2 bg-[#0d0d0d] border-t border-zinc-800 text-[10px] text-zinc-500 font-mono flex items-center justify-between select-none">
            <span>Extra fields outside schema are rejected</span>
            <span className="text-zinc-600">Esc to close</span>
          </div>
        </PortalPopover>
      )}

      {/* Editor Canvas: Gutter + Syntax Highlighted pre + Overlay textarea */}
      <div
        className="relative flex bg-[#0d0d0d] font-mono text-xs overflow-hidden"
        style={{ minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px`, height: `${Math.min(Math.max(lines.length * 20 + 24, minHeight), maxHeight)}px` }}
      >
        {/* Line Numbers Gutter */}
        <div
          ref={gutterRef}
          className="w-10 py-2.5 pr-2.5 text-right bg-[#080808] text-zinc-600 select-none pointer-events-none border-r border-zinc-800/80 font-mono text-[11px] leading-5 overflow-hidden shrink-0"
        >
          {lines.map((_, idx) => (
            <div key={idx} className="h-5 leading-5">
              {idx + 1}
            </div>
          ))}
        </div>

        {/* Code Canvas */}
        <div className="relative flex-1 overflow-hidden h-full">
          {/* Syntax Highlighted Render Layer */}
          <pre
            ref={preRef}
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none p-2.5 m-0 font-mono text-xs leading-5 whitespace-pre overflow-hidden select-none border-0"
            style={{ tabSize: 2, MozTabSize: 2 }}
          >
            {lines.map((line, idx) => {
              const tokens = tokenizeJsonLine(line);
              return (
                <div key={idx} className="h-5 leading-5 whitespace-pre">
                  {tokens.length === 0 ? (
                    '\u00A0'
                  ) : (
                    tokens.map((token, tIdx) => (
                      <span key={tIdx} className={getTokenClass(token.type)}>
                        {token.text}
                      </span>
                    ))
                  )}
                </div>
              );
            })}
          </pre>

          {/* Live Interactive Textarea Overlay */}
          <textarea
            ref={textareaRef}
            value={jsonText}
            disabled={disabled}
            onChange={(e) => setJsonText(e.target.value)}
            onKeyDown={handleKeyDown}
            onScroll={handleScroll}
            spellCheck={false}
            autoCapitalize="off"
            autoComplete="off"
            autoCorrect="off"
            wrap="off"
            placeholder="Enter valid JSON..."
            className="absolute inset-0 w-full h-full p-2.5 m-0 bg-transparent text-transparent caret-white font-mono text-xs leading-5 whitespace-pre resize-none focus:outline-none overflow-auto border-0 selection:bg-sky-500/30 selection:text-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ tabSize: 2, MozTabSize: 2 }}
          />
        </div>
      </div>

      {/* Validation Error Banner */}
      {!isValid && (
        <div className="p-2.5 bg-rose-950/40 border-t border-rose-500/30 text-[11px] text-rose-300 font-mono space-y-1 max-h-32 overflow-y-auto">
          {syntaxError && (
            <div className="flex items-start gap-1.5">
              <AlertCircle size={13} className="text-rose-400 shrink-0 mt-0.5" />
              <span>Syntax Error: {syntaxError}</span>
            </div>
          )}
          {schemaErrors.map((err, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <span className="text-rose-500 font-bold shrink-0">•</span>
              <span>{err}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
